import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Phone,
  Mail,
  Calendar,
  Clock,
  MapPin,
  Minus,
  Plus,
  Ticket,
  TextSelect,
  CornerLeftDownIcon,
  TicketCheck,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";

const HoliPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    userName: "",
    phoneNumber: "",
    email: "",
    tickets: {
      category_0_5: 0,
      category_5_10: 0,
      category_10_above: 0,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ticket prices
  const ticketPrices = {
    category_0_5: 0,
    category_5_10: 600,
    category_10_above: 800,
  };

  // Calculate total amount
  const calculateTotal = () => {
    return (
      formData.tickets.category_0_5 * ticketPrices.category_0_5 +
      formData.tickets.category_5_10 * ticketPrices.category_5_10 +
      formData.tickets.category_10_above * ticketPrices.category_10_above
    );
  };

  // Calculate total tickets
  const getTotalTickets = () => {
    return (
      formData.tickets.category_0_5 +
      formData.tickets.category_5_10 +
      formData.tickets.category_10_above
    );
  };

  // Handle ticket increment/decrement
  const updateTicketCount = (category, increment) => {
    setFormData((prev) => ({
      ...prev,
      tickets: {
        ...prev.tickets,
        [category]: Math.max(0, prev.tickets[category] + (increment ? 1 : -1)),
      },
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.userName || !formData.phoneNumber) {
      alert("Please fill in all required fields");
      return;
    }

    if (getTotalTickets() === 0) {
      alert("Please select at least one ticket");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${apiUrl}/event/create`, {
        userName: formData.userName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        totalAmount: calculateTotal(),
        paidAmount: 0,
        paymentMode: "cash",
        tickets: formData.tickets,
      });

      if (response.data.success) {
        alert("Booking created successfully! Our team will contact you soon.");
        setIsModalOpen(false);
        // Reset form
        setFormData({
          userName: "",
          phoneNumber: "",
          email: "",
          tickets: {
            category_0_5: 0,
            category_5_10: 0,
            category_10_above: 0,
          },
        });
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      alert(error.response?.data?.message || "Failed to create booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      // Prevent scrolling on mount
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable scrolling when modal closes
      document.body.style.overflow = "unset";
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
      {/* Hero Header Section with Background Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://marketplace.canva.com/EAFcCsHKRtY/5/0/1131w/canva-colorful-holi-party-poster-SUTEX5mepkg.jpg"
            alt="Holi Festival"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-900/80 via-pink-900/70 to-purple-900/80" />
        </div>

        {/* Animated Color Splashes */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }}
          className="absolute top-20 right-20 w-96 h-96 bg-pink-500 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 6, repeat: Infinity, delay: 1 }}
          className="absolute bottom-20 left-20 w-96 h-96 bg-orange-500 rounded-full blur-3xl"
        />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-7xl mx-auto">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="flex justify-center mb-6"
          >
            <Ticket className="w-16 h-16 text-yellow-400 drop-shadow-2xl" />
          </motion.div>

          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-5xl sm:text-7xl md:text-9xl font-bold mb-4 text-yellow-300 drop-shadow-2xl tracking-wider"
            style={{ fontFamily: "'Cinzel Decorative', serif" }}
          >
            RANG-E-FUNINGO
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-3xl sm:text-4xl md:text-5xl text-orange-200 mb-4"
            style={{ fontFamily: "'Noto Serif Devanagari', serif" }}
          >
            रंगों का राजसी उत्सव २०२६
          </motion.p>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-12"
          >
            <div className="inline-block bg-gradient-to-r from-orange-600/80 to-pink-600/80 backdrop-blur-sm px-8 py-3 rounded-full border-2 border-yellow-400/50">
              <p className="text-xl sm:text-2xl text-white font-semibold tracking-wide">
                Royal Rajasthani Holi Celebration
              </p>
            </div>
          </motion.div>

          {/* Enroll Button */}
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.7 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="group relative px-10 sm:px-16 py-5 sm:py-6 bg-gradient-to-r from-pink-600 via-orange-500 to-yellow-500 text-white text-xl sm:text-3xl font-bold rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 border-4 border-yellow-300/50"
          >
            <span className="relative z-10 flex items-center gap-4">
              Enroll Now
              <Ticket className="w-7 h-7 sm:w-8 sm:h-8" />
            </span>
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 rounded-full blur-xl"
            />
          </motion.button>
        </div>
      </section>

      {/* Event Highlights Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-4xl sm:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: "'Cinzel Decorative', serif" }}
            >
              Event Highlights
            </h2>
            <p className="text-xl text-yellow-200">आयोजन की मुख्य बातें</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Date */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/20 backdrop-blur-sm p-6 rounded-3xl border-4 border-white/40 text-center"
            >
              <Calendar className="w-12 h-12 text-yellow-300 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">Event Date</h3>
              <p className="text-yellow-200 text-2xl font-bold">
                14th March 2026
              </p>
              <p className="text-white/80 mt-2">होली पर्व</p>
            </motion.div>

            {/* Time */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/20 backdrop-blur-sm p-6 rounded-3xl border-4 border-white/40 text-center"
            >
              <Clock className="w-12 h-12 text-yellow-300 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">Timing</h3>
              <p className="text-yellow-200 text-2xl font-bold">
                10:00 AM - 6:00 PM
              </p>
              <p className="text-white/80 mt-2">8 Hours Non-Stop Fun!</p>
            </motion.div>

            {/* Location */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/20 backdrop-blur-sm p-6 rounded-3xl border-4 border-white/40 text-center sm:col-span-2 lg:col-span-1"
            >
              <MapPin className="w-12 h-12 text-yellow-300 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">Location</h3>
              <p className="text-yellow-200 text-xl font-bold">
                Funingo Adventure Park
              </p>
              <p className="text-white/80 mt-2">Jabalpur, India</p>
            </motion.div>
          </div>

          {/* Pricing Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12 bg-white/30 backdrop-blur-sm p-8 rounded-3xl border-4 border-yellow-300"
          >
            <h3 className="text-center text-white text-3xl font-bold mb-6">
              Ticket Pricing
            </h3>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-6xl mb-2">👶</div>
                <p className="text-white font-bold text-lg">0-5 Years</p>
                <p className="text-yellow-200 text-4xl font-bold">FREE</p>
              </div>
              <div className="text-center">
                <div className="text-6xl mb-2">👧👦</div>
                <p className="text-white font-bold text-lg">5-10 Years</p>
                <p className="text-yellow-200 text-4xl font-bold">₹600</p>
              </div>
              <div className="text-center">
                <div className="text-6xl mb-2">👨‍👩‍👧‍👦</div>
                <p className="text-white font-bold text-lg">Above 10 Years</p>
                <p className="text-yellow-200 text-4xl font-bold">₹800</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enrollment Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9998]"
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ scale: 0.5, opacity: 0, y: 100 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.5, opacity: 0, y: 100 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden pointer-events-auto max-h-[90vh] overflow-y-auto relative"
              >
                {/* Background Pattern with Low Opacity */}
                <div
                  className="absolute inset-0 opacity-5 pointer-events-none"
                  style={{
                    backgroundImage: `url("https://m.media-amazon.com/images/I/71GBX9jyWkL._AC_UF894,1000_QL80_.jpg")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />

                {/* Close Button */}
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute top-4 right-4 z-50 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
                >
                  <X className="w-6 h-6 text-white" />
                </button>

                {/* Modal Header */}
                <div className="relative bg-gradient-to-r from-pink-600 via-orange-500 to-purple-600 p-8 text-center">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Ticket className="w-16 h-16 text-yellow-300 mx-auto mb-4" />
                  </motion.div>
                  <h2
                    className="text-3xl sm:text-4xl font-bold text-white mb-2"
                    style={{ fontFamily: "'Cinzel Decorative', serif" }}
                  >
                    Book Your Tickets
                  </h2>
                  <p className="text-yellow-200 text-lg">अपनी टिकट बुक करें</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 sm:p-8 relative">
                  {/* Personal Details */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-gray-700 font-bold mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.userName}
                        onChange={(e) =>
                          setFormData({ ...formData, userName: e.target.value })
                        }
                        className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="Enter your name"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-bold mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phoneNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phoneNumber: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-bold mb-2">
                        Email (Optional)
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  {/* Ticket Selection */}
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      Select Tickets
                    </h3>

                    <div className="space-y-4">
                      {/* 0-5 Years */}
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-2xl border-2 border-green-200"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-lg text-gray-800">
                              0-5 Years 👶
                            </p>
                            <p className="text-green-600 font-bold text-2xl">
                              FREE
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() =>
                                updateTicketCount("category_0_5", false)
                              }
                              className="w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            >
                              <Minus className="w-5 h-5" />
                            </motion.button>
                            <span className="text-2xl font-bold text-gray-800 w-12 text-center">
                              {formData.tickets.category_0_5}
                            </span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() =>
                                updateTicketCount("category_0_5", true)
                              }
                              className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            >
                              <Plus className="w-5 h-5" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>

                      {/* 5-10 Years */}
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-2xl border-2 border-purple-200"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-lg text-gray-800">
                              5-10 Years 👧👦
                            </p>
                            <p className="text-purple-600 font-bold text-2xl">
                              ₹600
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() =>
                                updateTicketCount("category_5_10", false)
                              }
                              className="w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            >
                              <Minus className="w-5 h-5" />
                            </motion.button>
                            <span className="text-2xl font-bold text-gray-800 w-12 text-center">
                              {formData.tickets.category_5_10}
                            </span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() =>
                                updateTicketCount("category_5_10", true)
                              }
                              className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            >
                              <Plus className="w-5 h-5" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>

                      {/* Above 10 Years */}
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="bg-gradient-to-r from-orange-50 to-pink-50 p-5 rounded-2xl border-2 border-orange-200"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-lg text-gray-800">
                              Above 10 Years 👨‍👩‍👧‍👦
                            </p>
                            <p className="text-orange-600 font-bold text-2xl">
                              ₹800
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() =>
                                updateTicketCount("category_10_above", false)
                              }
                              className="w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            >
                              <Minus className="w-5 h-5" />
                            </motion.button>
                            <span className="text-2xl font-bold text-gray-800 w-12 text-center">
                              {formData.tickets.category_10_above}
                            </span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              type="button"
                              onClick={() =>
                                updateTicketCount("category_10_above", true)
                              }
                              className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            >
                              <Plus className="w-5 h-5" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>

                  {/* Total Summary */}
                  <div className="bg-gradient-to-r from-yellow-100 via-orange-100 to-pink-100 p-6 rounded-2xl border-3 border-orange-300 mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-bold text-lg">
                        Total Tickets:
                      </span>
                      <span className="text-2xl font-bold text-orange-600">
                        {getTotalTickets()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-bold text-lg">
                        Total Amount:
                      </span>
                      <span className="text-3xl font-bold text-orange-600">
                        ₹{calculateTotal()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      Payment will be collected by admin
                    </p>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-pink-600 via-orange-500 to-purple-600 text-white text-xl font-bold rounded-2xl shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 border-2 border-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                          className="w-6 h-6 border-3 border-white border-t-transparent rounded-full"
                        />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        Confirm Booking
                      </span>
                    )}
                  </motion.button>

                  {/* Contact Info */}
                </form>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HoliPage;
